import { useState, useEffect } from "react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Shield, Ban, AlertCircle, Image, Eye, Check, FileWarning } from "lucide-react";

const POLICY_SECTIONS = [
  {
    id: "prohibitedItems",
    title: "Prohibited Items",
    icon: Ban,
    color: "red",
    items: [
      "Illegal drugs and drug paraphernalia",
      "Weapons, firearms, ammunition, and explosives",
      "Human remains, body parts, or bodily fluids",
      "Endangered species or products derived from them",
      "Hazardous materials and recalled products",
      "Prescription medications without proper licensing",
      "Adult content and pornographic materials",
      "Items that promote hate, violence, or discrimination",
      "Government-issued IDs and official documents",
      "Lock-picking devices and surveillance equipment"
    ],
    checkLabel: "I understand and will not list any prohibited items"
  },
  {
    id: "stolenItems",
    title: "Stolen & Illegally Obtained Items",
    icon: FileWarning,
    color: "orange",
    items: [
      "Stolen merchandise or items obtained through theft",
      "Items received through fraudulent means",
      "Property that belongs to another person without authorization",
      "Items with removed or altered serial numbers"
    ],
    warning: "⚠️ Selling stolen items is a criminal offense and will be reported to authorities.",
    checkLabel: "I confirm all my items are legally obtained and owned by me"
  },
  {
    id: "counterfeitItems",
    title: "Counterfeit & Fake Products",
    icon: AlertCircle,
    color: "yellow",
    items: [
      "Counterfeit branded goods (fake designer items)",
      "Replica products misrepresented as authentic",
      "Knockoff electronics with fake brand names",
      "Unauthorized copies of software, games, or media",
      "Fake certificates, diplomas, or credentials"
    ],
    warning: "⚠️ Trademark violations result in immediate account termination and legal action.",
    checkLabel: "I will only sell authentic, genuine products"
  },
  {
    id: "copyrightImages",
    title: "Copyright - Images Policy",
    icon: Image,
    color: "blue",
    items: [
      "Only use photos you have taken yourself",
      "Do not use images from other sellers or websites",
      "Do not use stock photos without proper licensing",
      "Do not use manufacturer images without permission",
      "Watermarked images from other platforms are not allowed"
    ],
    tip: "📷 Take your own product photos for best results and legal compliance.",
    checkLabel: "I own or have permission for all images I upload"
  },
  {
    id: "copyrightVideos",
    title: "Copyright - Videos Policy",
    icon: Eye,
    color: "purple",
    items: [
      "Only upload videos you have created yourself",
      "Do not use copyrighted music without license",
      "Do not use clips from TV shows, movies, or other media",
      "Do not use another seller video content",
      "Promotional videos from manufacturers need written permission"
    ],
    checkLabel: "I own or have permission for all videos I upload"
  },
  {
    id: "accurateDescriptions",
    title: "Honest & Accurate Listings",
    icon: Check,
    color: "emerald",
    items: [
      "Product descriptions must be accurate and truthful",
      "Clearly state the condition of items (new, used, refurbished)",
      "Disclose all defects, damages, or issues",
      "Prices must reflect the actual selling price",
      "Do not use misleading titles or descriptions"
    ],
    checkLabel: "I will provide accurate and honest product descriptions"
  },
  {
    id: "legalCompliance",
    title: "Legal Compliance Agreement",
    icon: Shield,
    color: "slate",
    description: "By selling on Liantiao, I agree to:",
    items: [
      "Comply with all Jamaican laws and regulations",
      "Pay applicable taxes on my sales",
      "Maintain proper business records",
      "Respond to buyer inquiries within 48 hours",
      "Ship orders within the stated timeframe",
      "Accept returns per Liantiao return policy"
    ],
    checkLabel: "I agree to comply with all policies and legal requirements"
  }
];

export default function SellerPolicyModal({ open, onOpenChange, onAccept }) {
  const [checks, setChecks] = useState({});
  
  useEffect(() => {
    const initial = {};
    POLICY_SECTIONS.forEach(s => initial[s.id] = false);
    setChecks(initial);
  }, [open]);
  
  const allChecked = POLICY_SECTIONS.every(s => checks[s.id]);
  
  const handleAccept = () => {
    if (allChecked) {
      localStorage.setItem("liantiao_seller_policy_accepted", "true");
      onAccept();
    }
  };
  
  const colorMap = {
    red: { bg: "bg-red-950/30", border: "border-red-800/50", text: "text-red-400", check: "border-red-600 data-[state=checked]:bg-red-600" },
    orange: { bg: "bg-orange-950/30", border: "border-orange-800/50", text: "text-orange-400", check: "border-orange-600 data-[state=checked]:bg-orange-600" },
    yellow: { bg: "bg-yellow-950/30", border: "border-yellow-800/50", text: "text-yellow-400", check: "border-yellow-600 data-[state=checked]:bg-yellow-600" },
    blue: { bg: "bg-blue-950/30", border: "border-blue-800/50", text: "text-blue-400", check: "border-blue-600 data-[state=checked]:bg-blue-600" },
    purple: { bg: "bg-purple-950/30", border: "border-purple-800/50", text: "text-purple-400", check: "border-purple-600 data-[state=checked]:bg-purple-600" },
    emerald: { bg: "bg-emerald-950/30", border: "border-emerald-800/50", text: "text-emerald-400", check: "border-emerald-600 data-[state=checked]:bg-emerald-600" },
    slate: { bg: "bg-slate-800/50", border: "border-slate-700", text: "text-white", check: "border-emerald-600 data-[state=checked]:bg-emerald-600" }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-400" />
            Seller Policy Agreement
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-300">
            Before listing products on Liantiao, you must read and agree to our seller policies.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="space-y-4 py-4">
          {POLICY_SECTIONS.map(section => {
            const colors = colorMap[section.color];
            const Icon = section.icon;
            return (
              <div key={section.id} className={`${colors.bg} ${colors.border} border rounded-lg p-4`}>
                <h3 className={`font-bold ${colors.text} flex items-center gap-2 mb-2`}>
                  <Icon className="w-5 h-5" />
                  {section.title}
                </h3>
                {section.description && <p className="text-sm text-slate-300 mb-2">{section.description}</p>}
                <ul className="text-sm text-slate-400 space-y-1 ml-4 list-disc">
                  {section.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
                {section.warning && <p className={`text-sm ${colors.text} mt-2`}>{section.warning}</p>}
                {section.tip && <p className={`text-sm ${colors.text} mt-2`}>{section.tip}</p>}
                <div className="mt-3 flex items-center gap-2">
                  <Checkbox 
                    id={section.id}
                    checked={checks[section.id] || false}
                    onCheckedChange={(checked) => setChecks(p => ({...p, [section.id]: checked}))}
                    className={colors.check}
                  />
                  <label htmlFor={section.id} className="text-sm text-white cursor-pointer">
                    {section.checkLabel}
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel className="bg-slate-800 text-white border-slate-700 hover:bg-slate-700">
            Cancel
          </AlertDialogCancel>
          <Button
            onClick={handleAccept}
            disabled={!allChecked}
            className={allChecked ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700 cursor-not-allowed"}
          >
            <Shield className="w-4 h-4 mr-2" />
            I Accept All Policies
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
